/* Našeptávač institucí — jediné API, které smí UI volat.
 *
 * Pravidla, která tady drží (a hlídají je testy v __tests__/selektor.test.js):
 *   1) Typ produktu filtruje. U stavebního spoření se pojišťovna nenabídne, u pojištění
 *      penzijní společnost. Autorita je POVOLENE_KATEGORIE v katalog.js.
 *   2) Fulltext ignoruje diakritiku i velikost písmen: „ceska sporitelna" najde
 *      Českou spořitelnu, „wustenrot" najde Wüstenrot.
 *   3) „Jiná instituce" je VŽDY poslední volba, až za výsledky — poradce nesmí být
 *      tlačený vybrat si z katalogu, když tam jeho instituce není.
 *   4) Neaktivní instituce se nenabízejí. Dohledat je jde přes najdiInstituci
 *      s { vcetneNeaktivnich: true } — staré smlouvy na zaniklé značky existují.
 */

import { INSTITUCE, INSTITUCE_PODLE_ID, jeInstitucePovolenaProTyp, kategorieProTyp } from "./katalog";

/** Volba „u někoho jiného". Není to instituce, proto nemá partnerStatus ani kategorie. */
export const JINA_INSTITUCE = {
  institutionId: "jina",
  displayName: "Jiná instituce…",
  jina: true,
  legalName: null,
  institutionCategories: [],
  productCategories: [],
  active: true,
  partnerStatus: null,
  partnerStatusSource: null,
  aliases: [],
};

/** „Česká Spořitelna" → „ceska sporitelna". */
export function bezDiakritiky(text) {
  return String(text ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function tokeny(text) {
  return bezDiakritiky(text)
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/** Text, ve kterém se hledá: název + právní název + aliasy. */
function seno(inst) {
  return tokeny([inst.displayName, inst.legalName, ...(inst.aliases || [])].filter(Boolean).join(" "));
}

/**
 * Shoda dotazu s institucí. Každý token dotazu musí být PREFIXEM některého tokenu
 * instituce — schválně ne „obsahuje": jinak by dotaz „nn" vytáhl Colonnade.
 */
function sedi(inst, dotazTokeny) {
  if (!dotazTokeny.length) return true;
  const s = seno(inst);
  return dotazTokeny.every((t) => s.some((h) => h.startsWith(t)));
}

function skore(inst, dotazTokeny) {
  if (!dotazTokeny.length) return 2;
  const nazev = tokeny(inst.displayName);
  const cely = dotazTokeny.join(" ");
  if (nazev.join(" ").startsWith(cely)) return 0;
  if (nazev.some((h) => h.startsWith(dotazTokeny[0]))) return 1;
  return 2;
}

const abecedne = (a, b) => a.displayName.localeCompare(b.displayName, "cs");

/**
 * Instituce nabídnutelné k danému typu produktu (bez fulltextu).
 * typProduktu = null / "jine" → všechny aktivní.
 */
export function institucePodleTypuProduktu(typProduktu = null, { vcetneNeaktivnich = false } = {}) {
  return INSTITUCE.filter((i) => (vcetneNeaktivnich || i.active) && jeInstitucePovolenaProTyp(i, typProduktu)).sort(
    abecedne,
  );
}

/**
 * Našeptávač. Vrací pole institucí; poslední položka je JINA_INSTITUCE (lze vypnout).
 *
 * @param {string} dotaz          co poradce napsal (prázdné = celá povolená nabídka)
 * @param {object} opts
 *   typProduktu       filtr podle typu produktu (klíč z TYP_PRODUKTU_NA_KATEGORII)
 *   limit             max. počet skutečných institucí (default 20)
 *   sJinou            přidat „Jiná instituce…" na konec (default true)
 *   vcetneNeaktivnich zahrnout zaniklé značky (default false)
 */
export function hledejInstituce(dotaz = "", opts = {}) {
  const { typProduktu = null, limit = 20, sJinou = true, vcetneNeaktivnich = false } = opts;
  const dt = tokeny(dotaz);
  const nalezene = institucePodleTypuProduktu(typProduktu, { vcetneNeaktivnich })
    .filter((i) => sedi(i, dt))
    .map((i) => ({ i, s: skore(i, dt) }))
    .sort((a, b) => (a.s !== b.s ? a.s - b.s : abecedne(a.i, b.i)))
    .slice(0, Math.max(0, limit))
    .map((x) => x.i);

  return sJinou ? [...nalezene, JINA_INSTITUCE] : nalezene;
}

/**
 * Rozpoznání volného textu ze staré smlouvy („ČSOB penzijní") na instituci v katalogu.
 * Nic nehádá: když nesedí přesně název ani alias, vrací null (= neznámá instituce,
 * NE prázdná instituce).
 */
export function najdiInstituci(text, { vcetneNeaktivnich = true } = {}) {
  const hledane = tokeny(text).join(" ");
  if (!hledane) return null;
  const kandidati = INSTITUCE.filter((i) => vcetneNeaktivnich || i.active);
  const presne = kandidati.find((i) =>
    [i.displayName, i.legalName, ...(i.aliases || [])]
      .filter(Boolean)
      .some((n) => tokeny(n).join(" ") === hledane),
  );
  if (presne) return presne;
  return kandidati.find((i) => sedi(i, tokeny(text))) || null;
}

/** Instituce podle id (bez ohledu na aktivitu). */
export function instituceById(id) {
  return INSTITUCE_PODLE_ID[String(id ?? "")] || null;
}

/** Ploché názvy pro <datalist> — přesně to, co UI potřebuje pro autocomplete. */
export function nazvyInstituci(typProduktu = null) {
  return institucePodleTypuProduktu(typProduktu).map((i) => i.displayName);
}

export { kategorieProTyp };
